// frontend/app/components/lessonPlans/blocks/LessonGrowBlocks.js
//
// The two Grow-phase blocks of the Saras Learning Loop: mastery_map, which
// renders the student's own bands back to them and records nothing, and
// next_challenge, the bridge into the next Loop.
//
// The bars are hand-rolled Views on purpose — there is no chart library in this
// app, and a four-row bar list does not justify adding one.

import React from "react";
import { StyleSheet, Text, TextInput, View } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import { getMasteryBandMeta, getPhaseMeta, tint } from "../lessonPlanTheme";
import { BlockCard, BlockHeading, BlockPrompt, Pill } from "./LessonBlockShell";
import { ConceptTag, loopStyles } from "./LessonLoopShell";
import VoiceAnswerControl from "./VoiceAnswerControl";

/**
 * Grow phase. The student's own bands, supplied by the parent as
 * `[{ concept_key, label, mastery, band, evidence_count, next_step? }]`.
 *
 * `mastery === null` means nobody has checked this concept yet. That is not
 * zero, and drawing it as an empty bar would tell a student they know nothing
 * about something they were never asked about. `next_step` is the band
 * transition message; it is authored on the server so the student-facing copy
 * has one source, and nothing renders when it is absent.
 */
export const MasteryMapBlock = ({ block, colors, isDarkMode, mastery, t }) => {
  const translate = t || ((value) => value);
  const phase = getPhaseMeta("grow");
  const rows = Array.isArray(mastery) ? mastery : [];
  const scored = rows.filter((row) => typeof row?.mastery === "number");
  const showNextSteps = block.show_next_steps !== false;

  return (
    <BlockCard colors={colors} accent={phase.accent}>
      <BlockHeading
        colors={colors}
        icon={phase.icon}
        accent={phase.accent}
        title={block.title || translate("Where you are now")}
      />

      {scored.length === 0 ? (
        <Text style={[styles.emptyText, { color: colors.textSecondary }]}>
          {translate("We'll fill this in as you work.")}
        </Text>
      ) : (
        rows.map((row) => {
          const band = getMasteryBandMeta(row.band);
          const known = typeof row.mastery === "number";
          const pct = known ? Math.round(row.mastery * 100) : 0;
          const count = Number(row.evidence_count) || 0;

          return (
            <View key={row.concept_key} style={styles.masteryRow}>
              <View style={styles.masteryHeader}>
                <Text
                  style={[styles.masteryLabel, { color: colors.textPrimary }]}
                  numberOfLines={1}
                >
                  {row.label || row.concept_key}
                </Text>
                {known ? (
                  <Pill
                    colors={colors}
                    label={translate(band.label)}
                    accent={band.accent}
                    background={tint(band.accent, isDarkMode, 1.4)}
                  />
                ) : null}
              </View>

              {known ? (
                <View style={styles.masteryBarRow}>
                  <View
                    style={[styles.masteryTrack, { backgroundColor: colors.border }]}
                  >
                    <View
                      style={[
                        styles.masteryFill,
                        { backgroundColor: band.accent, width: `${pct}%` },
                      ]}
                    />
                  </View>
                  <Text style={[styles.masteryCount, { color: colors.textMuted }]}>
                    n={count}
                  </Text>
                </View>
              ) : (
                <Text style={[styles.masteryPending, { color: colors.textMuted }]}>
                  {translate("Not enough evidence yet")}
                </Text>
              )}

              {known && showNextSteps && row.next_step ? (
                <Text style={[styles.masteryNext, { color: colors.textSecondary }]}>
                  {row.next_step}
                </Text>
              ) : null}
            </View>
          );
        })
      )}
    </BlockCard>
  );
};

/** Grow phase. The bridge into the next Loop — recorded, never scored. */
export const NextChallengeBlock = ({
  block,
  colors,
  entry,
  onAnswer,
  readOnly,
  conceptLabels,
  language,
  onExplorationEvent,
  t,
}) => {
  const translate = t || ((value) => value);
  const phase = getPhaseMeta("grow");
  const value = typeof entry?.value === "string" ? entry.value : "";

  return (
    <BlockCard colors={colors} accent={phase.accent}>
      {block.next_loop_hint ? (
        <View style={styles.leadInRow}>
          <MaterialCommunityIcons
            name="arrow-right-circle-outline"
            size={16}
            color={phase.accent}
          />
          <Text style={[styles.leadInText, { color: phase.accent }]}>
            {block.next_loop_hint}
          </Text>
        </View>
      ) : null}

      <BlockPrompt colors={colors}>{block.prompt}</BlockPrompt>

      <VoiceAnswerControl
        colors={colors}
        accent={phase.accent}
        value={value}
        onChange={(next) => onAnswer(block, next)}
        readOnly={readOnly}
        language={language}
        onEvent={onExplorationEvent}
        t={t}
      />

      <TextInput
        value={value}
        onChangeText={(next) => onAnswer(block, next)}
        editable={!readOnly}
        multiline
        placeholder={block.placeholder || translate("Your first thoughts…")}
        placeholderTextColor={colors.textMuted}
        accessibilityLabel={block.prompt}
        style={[
          loopStyles.input,
          {
            backgroundColor: colors.subtleBg,
            borderColor: colors.border,
            color: colors.textPrimary,
          },
        ]}
      />

      <ConceptTag block={block} colors={colors} conceptLabels={conceptLabels} />
    </BlockCard>
  );
};

const styles = StyleSheet.create({
  emptyText: { fontSize: 13, lineHeight: 20 },

  masteryRow: { marginBottom: 12 },
  masteryHeader: { flexDirection: "row", alignItems: "center" },
  masteryLabel: { fontSize: 13, fontWeight: "700", flex: 1, marginRight: 8 },
  masteryBarRow: { flexDirection: "row", alignItems: "center", marginTop: 6 },
  masteryTrack: { flex: 1, height: 8, borderRadius: 4, overflow: "hidden" },
  masteryFill: { height: 8, borderRadius: 4 },
  masteryCount: { fontSize: 11, fontWeight: "700", marginLeft: 8, minWidth: 30 },
  masteryPending: { fontSize: 12, fontStyle: "italic", marginTop: 6 },
  masteryNext: { fontSize: 12, lineHeight: 18, marginTop: 6 },

  leadInRow: { flexDirection: "row", alignItems: "center", marginBottom: 8 },
  leadInText: {
    fontSize: 11,
    fontWeight: "800",
    letterSpacing: 0.4,
    marginLeft: 6,
    flex: 1,
  },
});
